import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, HelpCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

// FAQ content lives in i18n (faq.items.*). Sections group the questions; a few
// answers carry an internal link rendered after the answer paragraph.
const SECTIONS = [
  { key: "general", items: ["q1", "q2", "q3", "q4"] },
  { key: "clients", items: ["q5", "q6", "q7", "q8", "q9", "q10"] },
  { key: "fixybees", items: ["q11", "q12", "q13", "q14", "q15", "q16"] },
];
const ITEM_LINKS = {
  q5: { to: "/guides/post-a-job", labelKey: "faq.q5LinkText" },
  q12: { to: "/pricing", labelKey: "faq.q12LinkText" },
};

export default function FAQ() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="faq-page">
      <Navbar />
      <main className="section flex-1">
        <div className="container-app max-w-3xl">
          <Link to="/" className="fx-btn fx-btn--ghost text-sm mb-4 inline-flex" data-testid="back-home">
            <ArrowLeft size={14} /> {t("common.backHome")}
          </Link>
          <div className="flex items-center gap-2 mb-8">
            <HelpCircle className="text-hive-ink" />
            <h1 className="font-display text-4xl md:text-5xl font-extrabold text-worker">{t("faq.title")}</h1>
          </div>

          <div className="space-y-10">
            {SECTIONS.map((section) => (
              <section key={section.key} data-testid={`faq-section-${section.key}`}>
                <h2 className="font-display text-sm font-bold uppercase tracking-widest text-hive-ink mb-2">
                  {t(`faq.sections.${section.key}`)}
                </h2>
                <Accordion type="single" collapsible className="card-base px-5">
                  {section.items.map((id) => {
                    const link = ITEM_LINKS[id];
                    return (
                      <AccordionItem key={id} value={id} data-testid={`faq-${id}`}>
                        <AccordionTrigger className="text-base font-display font-semibold text-worker">
                          {t(`faq.items.${id}.q`)}
                        </AccordionTrigger>
                        <AccordionContent className="text-smoke leading-relaxed">
                          <p>{t(`faq.items.${id}.a`)}</p>
                          {link && (
                            <Link
                              to={link.to}
                              className="mt-3 inline-flex items-center gap-1 text-hive-ink font-medium hover:underline"
                              data-testid={`faq-${id}-link`}
                            >
                              {t(link.labelKey)} <ArrowRight size={14} />
                            </Link>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
